/**
 * iter653 — MenuItemEditSheet — extracted from MenuItemsPage.jsx.
 *
 * Right-side Foodics-style edit drawer with 7 tabs (Details / Sizes /
 * Add-ons / Options / Branches / Platforms / Schedule) and a sticky
 * footer. The parent still owns ALL state — this component is purely
 * presentational and dispatches changes back via callbacks. Keeping
 * state in the parent (vs. lifting into a local form) preserves the
 * existing controlled-input flow and avoids a refactor of handleSave.
 *
 * Why a single `props` blob instead of N individual props: the parent
 * already manages 30+ state slots for this form and the inline JSX
 * read them all. A single object makes the boundary visible at the
 * callsite and avoids 30-line prop lists. Each render passes the
 * SAME identity (`setFormData`, branches, platforms…) so React's
 * reference equality keeps re-renders cheap.
 */
import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, Building2, Globe, Check, Eye, EyeOff, Sparkles, Wand2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SuggestedPriceBanner } from '@/components/menu/SuggestedPriceBanner';

const SCHEDULE_DAYS = [
  { day: 0, label: 'Sun' }, { day: 1, label: 'Mon' }, { day: 2, label: 'Tue' },
  { day: 3, label: 'Wed' }, { day: 4, label: 'Thu' }, { day: 5, label: 'Fri' },
  { day: 6, label: 'Sat' },
];

export default function MenuItemEditSheet({
  open,
  onOpenChange,
  editingItem,
  formData,
  setFormData,
  saving,
  onSave,
  branches,
  platforms,
  allergenChoices,
  centralAddons,
  categories,
  cur,
  suggestedPriceBanner,
  setSuggestedPriceBanner,
  toggleBranch,
  togglePlatform,
  // iter653 — these sub-editors live in the parent file so they can
  // share local helpers/state. Pass them in as children-by-name to
  // avoid double-defining them.
  SizesEditor,
  AddonsLinker,
  OptionGroupsEditor,
  recipes = [],
  onAddCategory,
}) {
  // AI Identity Helper — Arabic name, description, allergen suggestions.
  // Reuses the app's Emergent LLM key via /menu-ai/* endpoints.
  const [aiBusy, setAiBusy] = useState('');
  const [allergenSuggestions, setAllergenSuggestions] = useState([]);
  const [arSuggested, setArSuggested] = useState(false);
  const [addingCat, setAddingCat] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  const catName = (categories.find(c => c.id === formData.category) || {}).name || '';

  // AI Cost Insight — flag a thin margin from the linked recipe cost and
  // suggest a profit-safe price (reuses recipes' auto-computed cost_per_unit).
  const TARGET_MARGIN = 0.30;
  const recipeById = Object.fromEntries((recipes || []).map(r => [r.id, r]));
  const linkedCost = () => {
    const links = formData.recipe_links || [];
    if (!links.length) return null;
    const def = links.find(l => !l.size_name) || links[0];
    const r = recipeById[def.recipe_id];
    if (!r) return null;
    return Number(r.cost_per_unit || 0) * Number(def.scale || 1);
  };

  const suggestArabic = async ({ silent = false } = {}) => {
    const name = (formData.name || '').trim();
    if (!name) { if (!silent) toast.error('Enter the English name first'); return; }
    setAiBusy('ar');
    try {
      const { data } = await api.post('/menu-ai/translate-name', { name });
      if (data.name_ar) { setFormData(f => ({ ...f, name_ar: data.name_ar })); if (!silent) toast.success('Arabic name suggested'); }
    } catch (e) { if (!silent) toast.error(e.response?.data?.detail || 'AI translation failed'); }
    finally { setAiBusy(''); }
  };

  const generateDescription = async () => {
    const name = (formData.name || '').trim();
    if (!name) { toast.error('Enter the English name first'); return; }
    setAiBusy('desc');
    try {
      const { data } = await api.post('/menu-ai/description', { name, category: catName });
      if (data.description) { setFormData(f => ({ ...f, description: data.description })); toast.success('Description generated'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'AI description failed'); }
    finally { setAiBusy(''); }
  };

  const suggestAllergens = async () => {
    const name = (formData.name || '').trim();
    if (!name) { toast.error('Enter the English name first'); return; }
    setAiBusy('allergens');
    try {
      const { data } = await api.post('/menu-ai/allergens', { name, description: formData.description });
      const already = formData.allergens || [];
      const fresh = (data.allergens || []).filter(a => !already.includes(a));
      setAllergenSuggestions(fresh);
      if (fresh.length === 0) toast.info('No new allergens suggested');
    } catch (e) { toast.error(e.response?.data?.detail || 'AI allergen suggestion failed'); }
    finally { setAiBusy(''); }
  };

  const addAllergen = (name) => {
    setFormData(prev => ({ ...prev, allergens: [...(prev.allergens || []), name] }));
    setAllergenSuggestions(prev => prev.filter(a => a !== name));
  };

  const saveNewCategory = async () => {
    const n = newCat.trim();
    if (!n || !onAddCategory) return;
    setSavingCat(true);
    try {
      const id = await onAddCategory(n);
      if (id) setFormData(f => ({ ...f, category: id }));
      setNewCat(''); setAddingCat(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add category'); }
    finally { setSavingCat(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl p-0 flex flex-col gap-0 overflow-hidden"
        data-testid="menu-item-edit-sheet"
      >
        <SheetHeader className="px-6 py-4 border-b bg-stone-50 dark:bg-stone-900/60 shrink-0">
          <SheetTitle className="font-outfit text-lg">{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {editingItem?.id === suggestedPriceBanner?.itemId && (
            <SuggestedPriceBanner
              banner={suggestedPriceBanner}
              onApply={() => {
                setFormData(f => ({ ...f, price: suggestedPriceBanner.suggestedPrice.toFixed(2) }));
                toast.success(`Price field updated to ${suggestedPriceBanner.suggestedPrice.toFixed(2)} — review then click Save.`);
                setSuggestedPriceBanner(null);
              }}
              onDismiss={() => setSuggestedPriceBanner(null)}
            />
          )}

          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full mb-4 flex-wrap h-auto gap-1">
              <TabsTrigger value="details" className="flex-1 min-w-[80px]">Details</TabsTrigger>
              <TabsTrigger value="sizes" className="flex-1 min-w-[80px]" data-testid="sizes-tab">Sizes</TabsTrigger>
              <TabsTrigger value="addons" className="flex-1 min-w-[80px]" data-testid="addons-tab">Add-ons</TabsTrigger>
              <TabsTrigger value="options" className="flex-1 min-w-[80px]" data-testid="options-tab">Options</TabsTrigger>
              <TabsTrigger value="branches" className="flex-1 min-w-[80px]" data-testid="branches-tab">Branches</TabsTrigger>
              <TabsTrigger value="platforms" className="flex-1 min-w-[80px]" data-testid="platforms-tab">Platforms</TabsTrigger>
              <TabsTrigger value="schedule" className="flex-1 min-w-[80px]" data-testid="schedule-tab">Schedule</TabsTrigger>
            </TabsList>

            {/* Details */}
            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name (English) *</Label>
                  <Input
                    value={formData.name}
                    onChange={e => { setFormData({ ...formData, name: e.target.value }); setArSuggested(false); }}
                    onBlur={() => { if (formData.name?.trim() && !formData.name_ar?.trim() && !arSuggested) { setArSuggested(true); suggestArabic({ silent: true }); } }}
                    placeholder="Chicken Shawarma"
                    data-testid="menu-item-name-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Name (Arabic)</Label>
                    <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-indigo-600" disabled={aiBusy === 'ar' || !formData.name?.trim()} onClick={() => suggestArabic()} data-testid="ai-suggest-arabic-btn">
                      {aiBusy === 'ar' ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Wand2 size={12} className="mr-1" />}AI
                    </Button>
                  </div>
                  <Input value={formData.name_ar} onChange={e => setFormData({ ...formData, name_ar: e.target.value })} placeholder="شاورما دجاج" dir="rtl" data-testid="menu-item-name-ar-input" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Description</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-indigo-600" disabled={aiBusy === 'desc' || !formData.name?.trim()} onClick={generateDescription} data-testid="ai-generate-description-btn">
                    {aiBusy === 'desc' ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />}AI Generate
                  </Button>
                </div>
                <Textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={2} data-testid="menu-item-description-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Category</Label>
                    {onAddCategory && (
                      <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-orange-600" onClick={() => setAddingCat(v => !v)} data-testid="menu-item-add-category-btn">
                        {addingCat ? <X size={12} className="mr-1" /> : <Plus size={12} className="mr-1" />}{addingCat ? 'Cancel' : 'New'}
                      </Button>
                    )}
                  </div>
                  {addingCat ? (
                    <div className="flex gap-1.5">
                      <Input value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveNewCategory()} placeholder="New category name" className="h-9" data-testid="menu-item-new-category-input" autoFocus />
                      <Button type="button" size="sm" className="h-9 bg-orange-500 hover:bg-orange-600" disabled={savingCat || !newCat.trim()} onClick={saveNewCategory} data-testid="menu-item-save-category-btn">
                        {savingCat ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                      </Button>
                    </div>
                  ) : (
                    <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                      <SelectTrigger data-testid="menu-item-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label>Price (SR) *</Label>
                  <Input type="number" step="0.01" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} data-testid="menu-item-price-input" />
                  <label className="flex items-center gap-2 mt-1.5 text-xs cursor-pointer" data-testid="price-includes-tax-toggle-wrapper">
                    <input
                      type="checkbox"
                      checked={!!formData.price_includes_tax}
                      onChange={e => setFormData({ ...formData, price_includes_tax: e.target.checked })}
                      className="w-3.5 h-3.5 rounded border-stone-300 text-primary focus:ring-primary cursor-pointer"
                      data-testid="price-includes-tax-toggle"
                    />
                    <span className="text-stone-600 dark:text-stone-300">
                      Price <strong>includes</strong> VAT
                      <span className="ml-1 text-stone-400">
                        ({formData.price_includes_tax
                          ? `customer pays ${cur()} ${parseFloat(formData.price || 0).toFixed(2)}`
                          : `VAT added at checkout`})
                      </span>
                    </span>
                  </label>
                  {(() => {
                    const cost = linkedCost();
                    const price = parseFloat(formData.price) || 0;
                    if (cost == null || cost <= 0 || price <= 0) return null;
                    const margin = (price - cost) / price;
                    if (margin >= TARGET_MARGIN) return null;
                    const safe = cost / (1 - TARGET_MARGIN);
                    return (
                      <div className="mt-1.5 text-[11px] rounded-md bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 p-2" data-testid="ai-cost-insight">
                        Thin margin ({(margin * 100).toFixed(0)}%). Recipe cost {cur()} {cost.toFixed(2)}. For a 30% margin, price ≥ {cur()} {safe.toFixed(2)}.
                        <Button type="button" size="sm" variant="ghost" className="h-6 ml-1 px-1.5 text-[11px] text-emerald-700 dark:text-emerald-400" onClick={() => setFormData(f => ({ ...f, price: safe.toFixed(2) }))} data-testid="ai-cost-insight-apply">
                          Apply {cur()} {safe.toFixed(2)}
                        </Button>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Cost Price</Label>
                    {(formData.recipe_links || []).some(l => l.recipe_id) && (
                      <label className="flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-400 cursor-pointer" data-testid="cost-auto-wrapper">
                        <input
                          type="checkbox"
                          checked={formData.cost_price_auto !== false}
                          onChange={e => setFormData({ ...formData, cost_price_auto: e.target.checked })}
                          className="w-3.5 h-3.5 rounded border-stone-300 text-primary focus:ring-primary cursor-pointer"
                          data-testid="cost-auto-toggle"
                        />
                        Auto from recipe
                      </label>
                    )}
                  </div>
                  {formData.cost_price_auto !== false && (formData.recipe_links || []).some(l => l.recipe_id) ? (
                    <>
                      <Input type="number" step="0.01" value={(linkedCost() || 0).toFixed(2)} readOnly disabled className="bg-muted/60" data-testid="menu-item-cost-input" />
                      <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">Locked to linked recipe cost — updates automatically on purchases.</p>
                    </>
                  ) : (
                    <Input type="number" step="0.01" value={formData.cost_price} onChange={e => setFormData({ ...formData, cost_price: e.target.value })} data-testid="menu-item-cost-input" />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Prep Time (min)</Label><Input type="number" value={formData.preparation_time} onChange={e => setFormData({ ...formData, preparation_time: e.target.value })} /></div>
                <div className="flex items-center justify-between p-3 bg-muted/50 dark:bg-muted rounded-lg">
                  <div><Label>Available</Label><p className="text-xs text-muted-foreground">Show in POS</p></div>
                  <Switch checked={formData.is_available} onCheckedChange={v => setFormData({ ...formData, is_available: v })} />
                </div>
              </div>
              {/* iter845i — Self-Service Online Ordering: per-item toggle,
                  independent from `is_available` (which gates the in-store POS).
                  Backend already honours this in /api/public/menu. */}
              <div className="flex items-center justify-between p-3 bg-emerald-50/60 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-lg">
                <div>
                  <Label>Available online</Label>
                  <p className="text-xs text-muted-foreground">Show on the customer-facing self-order menu (QR + shareable link)</p>
                </div>
                <Switch
                  checked={formData.available_online !== false}
                  onCheckedChange={v => setFormData({ ...formData, available_online: v })}
                  data-testid="available-online-switch"
                />
              </div>
              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mt-2">
                  {['popular', 'new', 'spicy', 'vegetarian'].map(tag => (
                    <Button key={tag} type="button" size="sm"
                      variant={formData.tags.includes(tag) ? 'default' : 'outline'}
                      className={formData.tags.includes(tag) ? 'bg-orange-500' : ''}
                      onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag] }))}>
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>

              <div data-testid="menu-item-allergens-picker">
                <div className="flex items-center justify-between">
                  <Label>Allergens</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-indigo-600" disabled={aiBusy === 'allergens' || !formData.name?.trim()} onClick={suggestAllergens} data-testid="ai-suggest-allergens-btn">
                    {aiBusy === 'allergens' ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Sparkles size={12} className="mr-1" />}AI Suggest
                  </Button>
                </div>
                {allergenSuggestions.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap items-center p-2 rounded-lg bg-indigo-50/60 border border-indigo-200/60" data-testid="allergen-ai-suggestions">
                    <span className="text-[11px] text-indigo-700 font-medium">AI suggests:</span>
                    {allergenSuggestions.map(a => (
                      <Button key={a} type="button" size="sm" variant="outline" className="h-6 text-[11px] border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                        onClick={() => addAllergen(a)} data-testid={`allergen-suggestion-${a}`}>
                        <Plus size={11} className="mr-0.5" />{a}
                      </Button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {allergenChoices.length === 0 ? (
                    <span className="text-xs text-muted-foreground italic">
                      No allergens configured. Add some at <a href="/allergens" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">/allergens</a>.
                    </span>
                  ) : allergenChoices.map(a => {
                    const on = (formData.allergens || []).includes(a.name);
                    return (
                      <Button
                        key={a.id}
                        type="button"
                        size="sm"
                        variant={on ? 'default' : 'outline'}
                        className={on ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          allergens: (prev.allergens || []).includes(a.name)
                            ? prev.allergens.filter(x => x !== a.name)
                            : [...(prev.allergens || []), a.name],
                        }))}
                        data-testid={`allergen-toggle-${a.name}`}
                      >
                        {a.icon && <span className="mr-1">{a.icon}</span>}
                        {a.name}
                      </Button>
                    );
                  })}
                </div>
                {(() => {
                  const choiceNames = new Set(allergenChoices.map(a => a.name));
                  const extra = (formData.allergens || []).filter(a => !choiceNames.has(a));
                  if (extra.length === 0) return null;
                  return (
                    <div className="flex gap-2 mt-2 flex-wrap" data-testid="allergen-extra-selected">
                      {extra.map(a => (
                        <Button key={a} type="button" size="sm" className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => setFormData(prev => ({ ...prev, allergens: (prev.allergens || []).filter(x => x !== a) }))}
                          data-testid={`allergen-remove-${a}`}>
                          {a}<X size={11} className="ml-1" />
                        </Button>
                      ))}
                    </div>
                  );
                })()}
                <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" data-testid="allergens-reviewed-wrapper">
                  <input
                    type="checkbox"
                    checked={!!formData.allergens_reviewed}
                    onChange={e => setFormData({ ...formData, allergens_reviewed: e.target.checked })}
                    className="w-3.5 h-3.5 rounded border-stone-300 text-primary focus:ring-primary cursor-pointer"
                    data-testid="allergens-reviewed-toggle"
                  />
                  <span className="text-stone-600 dark:text-stone-300">Allergens reviewed (mark done even if none apply) — clears the compliance flag</span>
                </label>
              </div>
            </TabsContent>

            {/* Sizes */}
            <TabsContent value="sizes">
              {/* iter877 — has_sizes master toggle. When off, the item uses
                  a single price only (backend clears sizes[] on save). */}
              <div className="flex items-center justify-between p-3 mb-4 bg-orange-50/60 dark:bg-orange-950/20 border border-orange-200/60 dark:border-orange-900/40 rounded-lg">
                <div>
                  <Label>Enable size variations</Label>
                  <p className="text-xs text-muted-foreground">Turn off to sell this item at one price only.</p>
                </div>
                <Switch
                  checked={!!formData.has_sizes}
                  onCheckedChange={v => setFormData({ ...formData, has_sizes: v, ...(v ? {} : { sizes: [] }) })}
                  data-testid="has-sizes-toggle"
                />
              </div>
              {formData.has_sizes ? (
                <SizesEditor
                  sizes={formData.sizes}
                  onChange={sizes => setFormData({ ...formData, sizes })}
                  branches={formData.branch_ids?.length > 0 ? branches.filter(b => formData.branch_ids.includes(b.id)) : branches}
                  branchAvailability={formData.sizesBranchAvail}
                  onAvailChange={sizesBranchAvail => setFormData({ ...formData, sizesBranchAvail })}
                  branchPrices={formData.branch_prices}
                  onBranchPricesChange={branch_prices => setFormData({ ...formData, branch_prices })}
                  recipeLinks={formData.recipe_links || []}
                  recipeById={recipeById}
                />
              ) : (
                <div className="text-sm text-muted-foreground text-center py-8">
                  Size variations disabled. The item will sell at the single price entered under Details.
                </div>
              )}

              {/* Recipe & Production Phase 1 — link stock-driven recipe(s).
                  Default target + one per size, each with a scale multiplier. */}
              <div className="mt-6 pt-4 border-t space-y-2" data-testid="recipe-link-editor">
                <p className="text-sm font-semibold">Link Recipe (stock deduction)</p>
                <p className="text-xs text-muted-foreground">
                  Attach a stock-driven recipe so selling this item auto-deducts ingredients. Set a scale to reuse one recipe across sizes/portions (e.g. Large = 1.5×).
                </p>
                {(() => {
                  const targets = [{ key: '', label: 'Default' }].concat(
                    (formData.has_sizes ? (formData.sizes || []) : []).filter(s => s.name).map(s => ({ key: s.name, label: s.name }))
                  );
                  const links = formData.recipe_links || [];
                  const getLink = (k) => links.find(l => (l.size_name || '') === k) || {};
                  const setLink = (k, patch) => {
                    const others = links.filter(l => (l.size_name || '') !== k);
                    const cur = getLink(k);
                    const next = { size_name: k, recipe_id: cur.recipe_id, scale: cur.scale ?? 1, ...patch };
                    setFormData({ ...formData, recipe_links: next.recipe_id ? [...others, next] : others });
                  };
                  return targets.map(t => {
                    const link = getLink(t.key);
                    return (
                      <div key={t.key || 'default'} className="flex items-center gap-2" data-testid={`recipe-link-row-${t.key || 'default'}`}>
                        <span className="text-xs w-20 shrink-0 text-muted-foreground">{t.label}</span>
                        <Select value={link.recipe_id || 'none'} onValueChange={v => setLink(t.key, { recipe_id: v === 'none' ? '' : v })}>
                          <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`recipe-link-select-${t.key || 'default'}`}><SelectValue placeholder="No recipe" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No recipe</SelectItem>
                            {recipes.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.output_qty} {r.output_unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          type="number" step="0.01" min="0"
                          value={link.recipe_id ? (link.scale ?? 1) : ''}
                          disabled={!link.recipe_id}
                          onChange={e => setLink(t.key, { scale: parseFloat(e.target.value) || 0 })}
                          className="w-20 h-8 text-xs" placeholder="scale ×"
                          data-testid={`recipe-link-scale-${t.key || 'default'}`}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
            </TabsContent>
            <TabsContent value="addons">
              <AddonsLinker
                linkedAddons={formData.linkedAddonIds}
                onChange={linkedAddonIds => setFormData({ ...formData, linkedAddonIds })}
                centralAddons={centralAddons}
                branches={formData.branch_ids?.length > 0 ? branches.filter(b => formData.branch_ids.includes(b.id)) : branches}
                branchAvailability={formData.addonsBranchAvail}
                onAvailChange={addonsBranchAvail => setFormData({ ...formData, addonsBranchAvail })}
              />
            </TabsContent>

            {/* Options */}
            <TabsContent value="options">
              <OptionGroupsEditor
                groups={formData.optionGroups}
                onChange={optionGroups => setFormData({ ...formData, optionGroups })}
                branches={branches}
              />
            </TabsContent>

            {/* Branches */}
            <TabsContent value="branches" className="space-y-4">
              <div className="p-3 bg-info/10 dark:bg-blue-900/10 rounded-lg">
                <p className="text-xs text-info dark:text-blue-400">Select which branches can sell this item. Leave empty for <strong>all branches</strong>. Set branch-specific prices.</p>
              </div>
              {/* iter877 — Bulk-fill helpers for branch pricing. Admins can
                  push the global price into every selected branch in one tap,
                  or clear all overrides. Speeds up multi-country rollouts. */}
              {formData.branch_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center px-3 py-2 bg-muted/40 rounded-lg" data-testid="branch-price-bulk-tools">
                  <span className="text-xs text-muted-foreground">Quick set:</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => {
                      const g = parseFloat(formData.price) || 0;
                      const next = { ...formData.branch_prices };
                      formData.branch_ids.forEach(bid => { next[bid] = g; });
                      setFormData({ ...formData, branch_prices: next });
                    }}
                    data-testid="branch-prices-copy-global"
                  >
                    Copy global price to all
                  </Button>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Bulk value"
                    className="w-24 h-7 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        const v = parseFloat(e.target.value);
                        const next = { ...formData.branch_prices };
                        formData.branch_ids.forEach(bid => { next[bid] = v; });
                        setFormData({ ...formData, branch_prices: next });
                        e.target.value = '';
                      }
                    }}
                    data-testid="branch-prices-bulk-input"
                  />
                  <span className="text-[10px] text-muted-foreground">↵ to apply</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-rose-600 hover:text-rose-700"
                    onClick={() => setFormData({ ...formData, branch_prices: {} })}
                    data-testid="branch-prices-clear-all"
                  >
                    Clear overrides
                  </Button>
                </div>
              )}
              <div className="space-y-2">
                {branches.map(b => (
                  <div key={b.id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${formData.branch_ids.includes(b.id) ? 'bg-orange-50 border-orange-300 dark:bg-orange-900/10 dark:border-orange-700' : 'dark:border-stone-700 hover:bg-muted/50 dark:hover:bg-muted'}`}
                    onClick={() => toggleBranch(b.id)} data-testid={`branch-toggle-${b.id}`}>
                    <Checkbox checked={formData.branch_ids.includes(b.id)} onCheckedChange={() => toggleBranch(b.id)} />
                    <Building2 size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium dark:text-white flex-1">{b.name}</span>
                    {formData.branch_ids.includes(b.id) && (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <Label className="text-xs whitespace-nowrap">Price:</Label>
                        <Input type="number" step="0.01" className="w-24 h-8 text-sm"
                          placeholder={formData.price || 'Same'}
                          value={formData.branch_prices[b.id] || ''}
                          onChange={e => setFormData(prev => ({ ...prev, branch_prices: { ...prev.branch_prices, [b.id]: e.target.value ? parseFloat(e.target.value) : undefined } }))}
                          data-testid={`branch-price-${b.id}`}
                        />
                      </div>
                    )}
                    {formData.branch_ids.includes(b.id) && !formData.branch_prices[b.id] && <Check size={16} className="text-orange-500" />}
                  </div>
                ))}
              </div>
              {formData.branch_ids.length === 0 && <Badge className="bg-success/20 text-success text-xs">Available at all branches</Badge>}
            </TabsContent>

            {/* Platforms */}
            <TabsContent value="platforms" className="space-y-4">
              <div className="p-3 bg-purple-50 dark:bg-purple-900/10 rounded-lg">
                <p className="text-xs text-purple-700 dark:text-purple-400">Select delivery platforms. Set different prices per platform.</p>
              </div>
              <div className="space-y-2">
                {platforms.filter(p => p.name !== 'Other').map(p => (
                  <div key={p.id} className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${formData.platform_ids.includes(p.id) ? 'bg-purple-50 border-purple-300 dark:bg-purple-900/10 dark:border-purple-700' : 'dark:border-stone-700'}`}>
                    <Checkbox checked={formData.platform_ids.includes(p.id)} onCheckedChange={() => togglePlatform(p.id)} />
                    <Globe size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium dark:text-white flex-1">{p.name} <span className="text-xs text-muted-foreground">{p.name_ar}</span></span>
                    {formData.platform_ids.includes(p.id) && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs whitespace-nowrap">Platform Price:</Label>
                        <Input type="number" step="0.01" className="w-24 h-8 text-sm"
                          placeholder={formData.price || 'Same'}
                          value={formData.platform_prices[p.id] || ''}
                          onChange={e => setFormData(prev => ({ ...prev, platform_prices: { ...prev.platform_prices, [p.id]: e.target.value ? parseFloat(e.target.value) : undefined } }))}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Schedule */}
            <TabsContent value="schedule" className="space-y-4">
              <div className="p-3 bg-warning/10 dark:bg-amber-900/10 rounded-lg">
                <p className="text-xs text-warning dark:text-amber-400">Set specific days and hours when this item is available. Perfect for <strong>daily specials</strong> or <strong>limited-time items</strong>.</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 dark:bg-muted rounded-lg">
                <div>
                  <Label className="font-semibold">Enable Schedule</Label>
                  <p className="text-xs text-muted-foreground">Restrict when this item can be ordered</p>
                </div>
                <Switch checked={formData.schedule?.enabled || false} onCheckedChange={v => setFormData(prev => ({ ...prev, schedule: { ...(prev.schedule || {}), enabled: v } }))} data-testid="schedule-enabled-switch" />
              </div>

              {formData.schedule?.enabled && (
                <>
                  <div>
                    <Label className="font-semibold mb-2 block">Available Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {SCHEDULE_DAYS.map(({ day, label }) => {
                        const days = formData.schedule?.available_days || [];
                        const isSelected = days.includes(day);
                        return (
                          <button key={day} type="button" data-testid={`schedule-day-${day}`}
                            onClick={() => {
                              const current = formData.schedule?.available_days || [];
                              const updated = isSelected ? current.filter(d => d !== day) : [...current, day].sort();
                              setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, available_days: updated } }));
                            }}
                            className={`w-12 h-12 rounded-xl text-xs font-bold border-2 transition-all ${
                              isSelected
                                ? 'bg-primary text-white border-primary shadow-sm'
                                : 'bg-white dark:bg-muted text-muted-foreground border-border dark:border-stone-600 hover:border-primary/50'
                            }`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, available_days: [0, 1, 2, 3, 4, 5, 6] } }))}>Select All</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, available_days: [1, 2, 3, 4] } }))}>Weekdays Only</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, available_days: [5, 6, 0] } }))}>Weekends Only</Button>
                    </div>
                  </div>

                  <div>
                    <Label className="font-semibold mb-2 block">Available Time Window</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">From</Label>
                        <Input type="time" value={formData.schedule?.start_time || '00:00'}
                          onChange={e => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, start_time: e.target.value } }))}
                          data-testid="schedule-start-time" />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">To</Label>
                        <Input type="time" value={formData.schedule?.end_time || '23:59'}
                          onChange={e => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, end_time: e.target.value } }))}
                          data-testid="schedule-end-time" />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, start_time: '00:00', end_time: '23:59' } }))}>All Day</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, start_time: '11:00', end_time: '15:00' } }))}>Lunch (11-3)</Button>
                      <Button type="button" size="sm" variant="ghost" className="text-xs"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, start_time: '18:00', end_time: '23:00' } }))}>Dinner (6-11)</Button>
                    </div>
                  </div>

                  <div>
                    <Label className="font-semibold mb-2 block">When Unavailable in POS</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" data-testid="behavior-disabled"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, unavailable_behavior: 'disabled' } }))}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.schedule?.unavailable_behavior === 'disabled' ? 'border-primary bg-primary/5' : 'border-border dark:border-stone-600 hover:border-primary/50'
                        }`}>
                        <EyeOff size={16} className="mb-1 text-muted-foreground" />
                        <p className="text-sm font-semibold">Show as Disabled</p>
                        <p className="text-[11px] text-muted-foreground">Visible but greyed out, can&apos;t be ordered</p>
                      </button>
                      <button type="button" data-testid="behavior-hide"
                        onClick={() => setFormData(prev => ({ ...prev, schedule: { ...prev.schedule, unavailable_behavior: 'hide' } }))}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          formData.schedule?.unavailable_behavior === 'hide' ? 'border-primary bg-primary/5' : 'border-border dark:border-stone-600 hover:border-primary/50'
                        }`}>
                        <Eye size={16} className="mb-1 text-muted-foreground" />
                        <p className="text-sm font-semibold">Completely Hide</p>
                        <p className="text-[11px] text-muted-foreground">Not visible in POS at all</p>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <SheetFooter className="px-6 py-3 border-t bg-stone-50 dark:bg-stone-900/60 shrink-0 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-menu-item-btn">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-menu-item-btn">
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
            {editingItem ? 'Update' : 'Create'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
